import type { GraphUserProfile } from "@/shared/types";

const CACHE_DURATION_MS = 24 * 60 * 60 * 1000;

const CACHE_KEY_PROFILE = "helpitech_graph_profile";
const CACHE_KEY_PHOTO = "helpitech_graph_photo";
const CACHE_KEY_TIMESTAMP = "helpitech_graph_ts";

/**
 * Recupera o perfil do usuário do cache localStorage.
 *
 * @returns O perfil cacheado ou `null` se não existir.
 *
 * @example
 * ```typescript
 * const profile = getCachedProfile();
 * ```
 */
export function getCachedProfile(): GraphUserProfile | null {
  const raw = localStorage.getItem(CACHE_KEY_PROFILE);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as GraphUserProfile;
  } catch {
    return null;
  }
}

/**
 * Recupera a foto do usuário do cache localStorage.
 *
 * @returns A foto em Base64 ou `null` se não existir.
 *
 * @example
 * ```typescript
 * const photo = getCachedPhoto();
 * ```
 */
export function getCachedPhoto(): string | null {
  return localStorage.getItem(CACHE_KEY_PHOTO);
}

/**
 * Verifica se o cache do Graph ainda é válido (dentro da duração configurada).
 *
 * @returns `true` se o cache for válido, `false` caso contrário.
 *
 * @example
 * ```typescript
 * if (isCacheValid()) {
 *   // usar cache
 * }
 * ```
 */
export function isCacheValid(): boolean {
  const raw = localStorage.getItem(CACHE_KEY_TIMESTAMP);
  if (!raw) return false;
  return Date.now() - parseInt(raw, 10) < CACHE_DURATION_MS;
}

/**
 * Armazena perfil e foto no cache localStorage com timestamp atual.
 *
 * @param profile - O perfil do usuário do Graph.
 * @param photo - A foto em Base64 (opcional).
 *
 * @example
 * ```typescript
 * cacheProfileAndPhoto(profile, photoBase64);
 * ```
 */
export function cacheProfileAndPhoto(
  profile: GraphUserProfile,
  photo: string | null
): void {
  localStorage.setItem(CACHE_KEY_PROFILE, JSON.stringify(profile));
  if (photo) {
    localStorage.setItem(CACHE_KEY_PHOTO, photo);
  }
  localStorage.setItem(CACHE_KEY_TIMESTAMP, String(Date.now()));
}

/**
 * Atualiza apenas a foto no cache localStorage (sem alterar perfil ou timestamp).
 *
 * @param photo - A foto em Base64.
 *
 * @example
 * ```typescript
 * cachePhotoOnly(newPhotoBase64);
 * ```
 */
export function cachePhotoOnly(photo: string): void {
  localStorage.setItem(CACHE_KEY_PHOTO, photo);
}

/**
 * Remove todos os dados do Graph do cache localStorage.
 *
 * @example
 * ```typescript
 * clearGraphCache();
 * ```
 */
export function clearGraphCache(): void {
  localStorage.removeItem(CACHE_KEY_PROFILE);
  localStorage.removeItem(CACHE_KEY_PHOTO);
  localStorage.removeItem(CACHE_KEY_TIMESTAMP);
}
